import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import { TitleInfo } from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { SellerAgentFormData } from 'src/app/modules/cd-request/components/wholesale-cd-wizard/steps/seller-agent/seller-agent.component';
import {
  CdContactCopySource,
  CdContactCopySourceProvider,
  CdContactSnapshot
} from 'src/app/modules/cd-request/components/wholesale-cd-wizard/steps/cd-contact-copy';
import { copyToClipboard } from 'src/utils';

export interface TitleInformationFormData {
  companyName: string;
  companyLicenseId: string;
  titleCaseNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  contactLicense: string;
  contactEmail: string;
  contactPhone: string;
}

@Component({
  selector: 'title-information-step',
  templateUrl: './title-information.component.html',
  styleUrls: ['./title-information.component.scss'],
  standalone: false
})
export class TitleInformationComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChild('titleInfoForm') titleInfoForm: NgForm;

  @Input() titleInformation: TitleInfo | undefined;

  /** When set, a "Copy From" menu offers the contact data entered on the wizard's other steps. */
  @Input() copySourceProvider: CdContactCopySourceProvider | null = null;

  formData: TitleInformationFormData = {
    companyName: '',
    companyLicenseId: '',
    titleCaseNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    contactName: '',
    contactLicense: '',
    contactEmail: '',
    contactPhone: ''
  };

  stateOptions: EnumerationItem[] = [];
  isLoading: boolean = false;
  copySources: CdContactCopySource[] = [];

  constructor(
    protected readonly injector: Injector,
    private readonly _enumerationService: EnumerationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.stateOptions = this._enumerationService.states;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['titleInformation']) {
      this.populateFormFromApiData();
    }
  }

  validateForm(): boolean {
    if (this.titleInfoForm) {
      this.titleInfoForm.form.markAllAsTouched();
      return this.titleInfoForm.form.valid;
    }
    return true;
  }

  getFormData(): TitleInformationFormData {
    return { ...this.formData };
  }

  onCopyFromToggled(isOpen: boolean): void {
    if (isOpen) {
      this.copySources = this.copySourceProvider?.getContactCopySources('titleInfo') ?? [];
    }
  }

  copyFrom(source: CdContactCopySource): void {
    this.applySnapshot(source.snapshot);
    this.titleInfoForm?.form.markAsDirty();
  }

  copyContactInfo(): void {
    const lines = [
      this.formData.companyName,
      this.formData.streetAddress,
      `${this.formData.city || ''}, ${this.formData.state || ''} ${this.formData.zip || ''}`,
      `Contact: ${this.formData.contactName || ''}`,
      `Email: ${this.formData.contactEmail || ''}`,
      `Phone: ${this.formData.contactPhone || ''}`
    ].filter(l => l && String(l).trim() !== '' && String(l).trim() !== ',');
    copyToClipboard(lines.join('\n'));
  }

  private applySnapshot(snapshot: CdContactSnapshot): void {
    this.formData.companyName = snapshot.companyName;
    this.formData.companyLicenseId = snapshot.companyLicenseId;
    this.formData.streetAddress = snapshot.address;
    this.formData.city = snapshot.city;
    this.formData.state = snapshot.state;
    this.formData.zip = snapshot.zip;
    this.formData.contactName = snapshot.contactName;
    this.formData.contactLicense = snapshot.contactLicense;
    this.formData.contactEmail = snapshot.contactEmail;
    this.formData.contactPhone = snapshot.contactPhone;
  }

  private populateFormFromApiData(): void {
    if (!this.titleInformation) {
      return;
    }

    this.formData.companyName = this.titleInformation.companyName || '';
    this.formData.companyLicenseId = this.titleInformation.companyLicenseId || '';
    this.formData.titleCaseNumber = this.titleInformation.titleCaseNumber || '';
    this.formData.streetAddress = this.titleInformation.companyAddress || '';
    this.formData.city = this.titleInformation.city || '';
    this.formData.state = this.titleInformation.state || '';
    this.formData.zip = this.titleInformation.zip || '';
    this.formData.contactName = this.titleInformation.contactFullName || '';
    this.formData.contactLicense = this.titleInformation.contactLicenseId || '';
    this.formData.contactEmail = this.titleInformation.contactEmailAddress || '';
    this.formData.contactPhone = this.titleInformation.contactPhoneNumber || '';
  }
}
